import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Book {
  book_id: string;
  title: string;
  authors: string[];
  cover_url?: string;
}

interface PairwiseData {
  tier: number;
  bookId: string;
  needsComparison: boolean;
  placementState?: any;
  comparison?: {
    candidateBook: Book;
    progress: {
      low: number;
      high: number;
      total: number;
    };
  };
  finalPosition?: number;
}

export function usePairwiseRanking() {
  const [pairwiseData, setPairwiseData] = useState<PairwiseData | null>(null);
  const [loading, setLoading] = useState(false);

  const triggerPairwiseComparison = async (userId: string, bookId: string, rating: number) => {
    setLoading(true);
    
    try {
      const response = await supabase.functions.invoke('on-rating-saved', {
        body: {
          userId,
          bookId,
          rating
        }
      });

      if (response.error) {
        throw response.error;
      }

      const data = response.data as PairwiseData;
      
      if (data.needsComparison && data.comparison) {
        setPairwiseData(data);
        return true; // Indicates pairwise UI should be shown
      } else if (data.finalPosition) {
        toast.success(`Placed at #${data.finalPosition} in your ${rating}★ tier`);
      }
      
      return false; // No comparison needed
    } catch (error) {
      console.error('Error triggering pairwise comparison:', error);
      toast.error('Failed to prepare ranking comparison');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const clearPairwiseData = () => {
    setPairwiseData(null);
  };

  return {
    pairwiseData,
    loading,
    triggerPairwiseComparison,
    clearPairwiseData
  };
}
